import React from 'react';
import { Footnote, FootNoteRefProps_t } from '../components/FootNote';


export const KoopmanHighDimensionPage: React.FC<{}> = () => {

    const references: Array<FootNoteRefProps_t> = React.useMemo(() => [
        { id: "koopman", ref: "https://arxiv.org/pdf/1805.07472"},
        { id: "koopman_legged", ref: "https://arxiv.org/pdf/2411.14321"}, 
        { id: "driving", text: "start at 22:32 for overview of bicycle models in self-driving", ref: "https://youtu.be/avbwgAsjAn8?t=1352"}
    ], []);

    // This gets the reference number that we render next to the footnote.
    // It just finds the index of the footnote with the given id in the list.
    const f = React.useCallback((id: string) => {
    let index = references.findIndex(ref => ref.id === id);
    if (index === -1) {
        throw Error(`could not find reference: ${id} in list`);
    }
    return index + 1;
    }, [references]);
    
    return (
        <main>
            <h1>Learned Koopman Operators and Model-Predictive Control on Highly Non-Linear Systems</h1>
            <p>
                For MPC problems to be solved efficiently, the model of the system dynamics must be convex and 
                usually practitioners use a linear model. This has prevented MPC from being used on high-dimensional 
                non-linear systems.
            </p> 
            
            <p>
                Koopman Operators are a function that maps the system state to a higher-dimensional space where 
                the system dynamics are linear. Recent work has shown that this mapping can be learned which allows MPC to be used 
                on non-linear systems like fluids<Footnote id="koopman" f={f}/> and walking 
                robots<Footnote id="koopman_legged" f={f}/>.
            </p>
            <p>
                How non-linear of systems can we learn an effective Koopman Operator for?
            </p>
            <p>
                Linear vs non-linear is a binary categorization of course but some systems are closer to 
                being linear systems than others. For example self-driving cars often use a bicycle model<Footnote id="driving" f={f}/> which
                is non-linear but good-enough solutions can be found efficiently enough to use MPC in real time. 
                Legged robots have contacts which are not as forgiving. There is work showing with careful learning, 
                legged robots can be controlled in simulation<Footnote id="koopman_legged" f={f}/> with learned 
                Koopman Operators.
            </p>
            <p>
                Here's a few ideas of systems to try:
            </p>
            <ol>
                <li>High-dimensional non-linear systems</li>
                <li>Hybrid systems</li>
                <li>Discrete systems</li>
                <li>Systems with contacts (robotic arm, biped etc.)</li>
                <li>Games like Atari</li>
            </ol>
            <p>
                I'm curious how general of a framework this is? As long as we have a goal state or a planner, could we use this
                as a general reinforcement learning algorithm? 
            </p>
            <p>
                Could this be combined with language models (which are 
                predictors of the next token i.e. a highly non-linear model of language) to 
                guide chatbot output towards desired outcomes without changing the prompt or fine-tuning? 
            </p>
        </main>
    )
};
